import { InteractionRequiredAuthError, type IPublicClientApplication } from '@azure/msal-browser';
import type { AccessTokenProvider } from '../api/client';
import { ApiError } from '../api/client';
import { apiScopes, popupRedirectUri } from './authConfig';

/**
 * Token provider backed by MSAL. Lives here rather than inside <c>ApiClient</c> so the client itself
 * has no auth dependency and can be pointed at the credential-free local mode.
 */
export function createMsalTokenProvider(
  msal: IPublicClientApplication,
  accountHomeId: string,
): AccessTokenProvider {
  return async () => {
    const account = msal.getAccount({ homeAccountId: accountHomeId }) ?? msal.getAllAccounts()[0];

    if (!account) {
      throw new ApiError(401, 'Your session has ended. Sign in again.');
    }

    try {
      const result = await msal.acquireTokenSilent({ scopes: apiScopes, account });
      return result.accessToken;
    } catch (error) {
      // A silent refresh cannot always succeed — conditional access, MFA re-prompts and expired
      // refresh tokens all land here. A popup rather than a redirect, so a form that is already
      // filled in survives the re-authentication.
      if (error instanceof InteractionRequiredAuthError) {
        const result = await msal.acquireTokenPopup({
          scopes: apiScopes,
          account,
          redirectUri: popupRedirectUri,
        });

        return result.accessToken;
      }

      throw error;
    }
  };
}

/**
 * Token provider for local development mode: there is no token, and the API is not asking for one.
 */
export const noAuthTokenProvider: AccessTokenProvider = () => Promise.resolve(null);
